import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { makeApiCall } from '../../config/api';

const WinterJobReport = () => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate.toISOString());
      if (toDate) params.append('to_date', toDate.toISOString());

      const response = await makeApiCall(`api/reports/winter-jobs?${params}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          setError(t('reports.error.fetching'));
        }
      } else {
        setError(t('reports.error.fetching'));
      }
    } catch (err) {
      console.error('Error fetching winter jobs report:', err);
      setError(t('reports.error.fetching'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setGeneratingPDF(true);
    setError(null);
    try {
      const response = await makeApiCall('api/reports/winter-jobs/pdf', {
        method: 'POST',
        body: JSON.stringify({
          from_date: fromDate ? fromDate.toISOString() : null,
          to_date: toDate ? toDate.toISOString() : null,
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `winter-jobs-report-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // TKT-251: surface the actual server message for 413
        // (PDF_ROW_CAP_EXCEEDED). Other errors fall back to the
        // generic toast.
        const data = await response.json().catch(() => ({}));
        setError(data.error || t('reports.error.pdf'));
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('reports.error.pdf'));
    } finally {
      setGeneratingPDF(false);
    }
  };

  // TKT-250: Excel export (Naomi). Mirrors downloadPDF but hits the
  // exceljs-backed /xlsx endpoint, which has no row cap.
  const downloadExcel = async () => {
    setGeneratingExcel(true);
    setError(null);
    try {
      const response = await makeApiCall('api/reports/winter-jobs/xlsx', {
        method: 'POST',
        body: JSON.stringify({
          from_date: fromDate ? fromDate.toISOString() : null,
          to_date: toDate ? toDate.toISOString() : null,
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `winter-jobs-report-${Date.now()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || t('reports.error.excel'));
      }
    } catch (err) {
      console.error('Error generating Excel:', err);
      setError(t('reports.error.excel'));
    } finally {
      setGeneratingExcel(false);
    }
  };

  useEffect(() => {
    // Auto-load last 30 days on mount
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    setFromDate(from);
    setToDate(to);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchReport();
    }
  }, []);

  // TKT-365: materials used on the visit (only the ones with an amount).
  const formatMaterials = (job) => [
    [t('winterLogs.salt'), job.salt_lbs, t('winterLogs.lbs')],
    [t('winterLogs.liquid'), job.liquid_litres, t('winterLogs.litres')],
    [t('winterLogs.iceMelter'), job.ice_melter_lbs, t('winterLogs.lbs')],
    [t('winterLogs.gritSand'), job.grit_sand_lbs, t('winterLogs.lbs')],
  ].filter(([, amount]) => Number(amount) > 0)
    .map(([label, amount, unit]) => `${label} ${Number(amount)} ${unit}`)
    .join(', ');

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Group data by contact
  const groupedData = {};
  data.forEach(job => {
    const contactId = job.contact_id || 'unknown';
    if (!groupedData[contactId]) {
      groupedData[contactId] = {
        contact: {
          first_name: job.first_name || '',
          last_name: job.last_name || '',
          company_name: job.company_name || '',
          street_no: job.street_no || '',
          city: job.city || '',
          postal_code: job.postal_code || ''
        },
        jobs: []
      };
    }
    groupedData[contactId].jobs.push(job);
  });

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value ? new Date(e.target.value) : null);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value ? new Date(e.target.value) : null);
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('reports.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label={t('reports.fromDate')}
              type="date"
              value={formatDateForInput(fromDate)}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label={t('reports.toDate')}
              type="date"
              value={formatDateForInput(toDate)}
              onChange={handleToDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={fetchReport}
                  disabled={loading || !fromDate || !toDate}
                  sx={{ bgcolor: '#1976d2' }}
                >
                  {t('reports.load')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={downloadPDF}
                  disabled={generatingPDF || data.length === 0}
                  sx={{ bgcolor: '#d32f2f' }}
                  aria-label={t('reports.downloadPDF')}
                >
                  {t('reports.downloadPDF')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={generatingExcel ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={downloadExcel}
                  disabled={generatingExcel || data.length === 0}
                  sx={{ bgcolor: '#1d6f42' }}
                  aria-label={t('reports.downloadExcel')}
                >
                  {t('reports.downloadExcel')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary */}
        {data.length > 0 && (
          <Card sx={{ mb: 3, bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                {t('reports.summary')}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip label={`${t('reports.totalJobs')}: ${data.length}`} color="primary" />
                <Chip
                  label={`${t('reports.uniqueCustomers')}: ${Object.keys(groupedData).length}`}
                  color="secondary"
                />
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Report Data */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : data.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {t('reports.noData')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('reports.selectDateRange')}
            </Typography>
          </Paper>
        ) : (
          <Box>
            {Object.values(groupedData).map((group, idx) => (
              <Card key={idx} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#474747' }}>
                    {group.contact.company_name || `${group.contact.first_name} ${group.contact.last_name}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {group.contact.street_no}, {group.contact.city} {group.contact.postal_code}
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>{t('reports.jobNumber')}</strong></TableCell>
                          <TableCell><strong>{t('reports.clockIn')}</strong></TableCell>
                          <TableCell><strong>{t('reports.clockOut')}</strong></TableCell>
                          <TableCell><strong>{t('reports.status')}</strong></TableCell>
                          <TableCell><strong>{t('reports.observations')}</strong></TableCell>
                          <TableCell><strong>{t('reports.materialsUsed')}</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.jobs.map((job) => (
                          <TableRow key={job.id}>
                            <TableCell>{job.job_number || 'N/A'}</TableCell>
                            <TableCell>{formatDate(job.clock_in_time)}</TableCell>
                            <TableCell>{formatDate(job.clock_out_time)}</TableCell>
                            <TableCell>
                              <Chip
                                label={job.status || 'N/A'}
                                size="small"
                                color={job.status === 'completed' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{job.observations || '-'}</TableCell>
                            <TableCell>{formatMaterials(job) || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
  );
};

export default WinterJobReport;

